/** Left-nav tree for /settings. Ported from dev SettingsTree.tsx + extended with
 *  per-agent dropdown so individual agents are selectable children under "Agents".
 *
 *  shadcn/Radix has no tree primitive; this is a minimal nav tree built on
 *  @/shared/ui/collapsible (Radix Collapsible) — just enough for one
 *  expandable "Agents" group, not a general-purpose tree API. */

import type { ReactNode } from 'react';
import { useMatch, NavLink } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/shared/lib/cn';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';
import { useSettingsAccess } from './access';

interface SettingsNavLinkProps {
  to: string;
  label: string;
  indent?: boolean;
  trailing?: ReactNode;
}

function SettingsNavLink({ to, label, indent, trailing }: SettingsNavLinkProps) {
  return (
    <NavLink
      to={to}
      className={({ isActive }: { isActive: boolean }) =>
        cn(
          'mb-0.5 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm no-underline hover:bg-accent',
          isActive ? 'font-bold text-foreground' : 'text-muted-foreground',
          indent && 'pl-6',
        )
      }
    >
      <span className="flex-1 truncate">{label}</span>
      {trailing}
    </NavLink>
  );
}

export default function SettingsTree() {
  const { canAccessAdmin, visibleAgents } = useSettingsAccess();
  const agentMatch = useMatch('/settings/agents/:agentId/*');
  const isAgentsRoute = agentMatch !== null;

  return (
    <div className="w-55 px-2 py-3">
      {canAccessAdmin && (
        <SettingsNavLink to="/settings/admin" label="Admin" />
      )}

      {visibleAgents.length > 0 && (
        <Collapsible defaultOpen>
          <CollapsibleTrigger
            className={cn(
              'flex w-full items-center gap-1 rounded-md px-2 py-1.5 text-sm hover:bg-accent [&[data-state=open]>svg]:rotate-90',
              isAgentsRoute ? 'font-bold text-foreground' : 'text-muted-foreground',
            )}
          >
            <ChevronRight className="size-3.5 shrink-0 transition-transform" aria-hidden="true" />
            Agents
          </CollapsibleTrigger>
          <CollapsibleContent>
            {visibleAgents.map((agent) => (
              <SettingsNavLink
                key={agent.id}
                to={`/settings/agents/${agent.id}/config`}
                label={agent.displayName}
                indent
              />
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
